import Breadcrumbs from 'Components/Breadcrumbs'
import CustomButton from 'Components/CustomButton'
import ListActions from 'Components/ListActions'
import ListSearch from 'Components/ListSearch'
import PageTitle from 'Components/PageTitle'
import { WithLoader } from 'Components/WithLoader'
import { PRODUCTS_EDIT_DRAWER_TAB_PARAM_KEY } from 'Services/ProductManagement/constants'
import useProductGroupOptions from 'Services/ProductManagement/Features/Parameter/Hooks/useProductGroupOptions'
import { useGetCustomerGroupParameterGroupQuery } from 'Services/ProductManagement/Features/Parameter/parameterApi'
import { ProductList } from 'Services/ProductManagement/Features/Product/Components/ProductList'
import {
  useProductsWithParams,
  useSingleProduct
} from 'Services/ProductManagement/Features/Product/Hooks'
import useProductListTabs from 'Services/ProductManagement/Features/Product/Hooks/useProductListTabs'
import { ProductDeleteModes } from 'Services/ProductManagement/Features/Product/productSlice'
import TariffProductDrawer from 'Services/ProductManagement/Features/Tariff/Components/TariffProductDrawer'
import TariffProductsEditDrawer from 'Services/ProductManagement/Features/Tariff/Components/TariffProductsEditDrawer'
import { useSelectedTariff } from 'Services/ProductManagement/Features/Tariff/Hooks'
import { useDeleteTariffProductMutation } from 'Services/ProductManagement/Features/Tariff/tariffApi'
import type { ProductTariffError } from 'Util/ApiClient'
import { UnsavedChangesProvider } from 'Util/Context/unsavedChangesContext'
import {
  IS_TARIFF_EDIT_ALLOWED,
  IS_WALTTI_SERVICE_ENVIRONMENT,
  PRODUCT_MANAGEMENT_ROOT_URL,
  ROWS_PER_PAGE_DEFAULT
} from 'Util/constants'
import useListFunctions from 'Util/hooks/useListFunctions'
import SnackbarUtils from 'Util/Snackbar'
import AddIcon from '@mui/icons-material/Add'
import { TabContext, TabList, TabPanel } from '@mui/lab'
import { Box, Tab } from '@mui/material'
import { unwrapResult } from '@reduxjs/toolkit'
import { useEffect, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { t } from 'ttag'

function TariffProductListPage() {
  const baseUrl = `${PRODUCT_MANAGEMENT_ROOT_URL}/tariffs`
  const navigate = useNavigate()
  const { id: tariffId } = useParams()
  const [searchParams, setSearchParams] = useSearchParams()

  const { singleTariff, isSingleTariffLoading } = useSelectedTariff(tariffId)

  const {
    tariffProducts,
    tariffProductsLoading,
    searchQueryParams,
    setSearchQueryParams,
    total_results,
    selectedProductId,
    setSelectedProductId,
    fetchTariffProductsWithParams,
    resetQueryParams
  } = useProductsWithParams({
    inTariff: tariffId
  })

  // Reset queryParams when tariffId is changed (from the sidebar menu)
  useEffect(() => {
    if (singleTariff && String(singleTariff.id) !== String(tariffId)) {
      resetQueryParams()
    }
  }, [tariffId, singleTariff])

  const { deleteProduct, deleteLoading: deleteProductLoading } = useSingleProduct(undefined)
  const [deleteTariffProduct, { isLoading: isDeleteTariffProductLoading }] =
    useDeleteTariffProductMutation()
  const [isProductsEditDrawerOpen, setIsProductsEditDrawerOpen] = useState(false)
  const [isSelectedListItemDrawerOpen, setIsSelectedListItemDrawerOpen] = useState(
    !!selectedProductId
  )

  const { searchText, handleChangePage, handleRequestSort, handleSearchTextChange, handleSearch } =
    useListFunctions({
      searchQueryParams,
      setSearchQueryParams
    })

  const { tabFromParams, handleTabChange } = useProductListTabs({
    setSearchQueryParams
  })

  const {
    data: customerGroupParameterGroup = null,
    isLoading: isCustomerGroupParameterGroupLoading
  } = useGetCustomerGroupParameterGroupQuery()

  const { productGroupOptions, isProductGroupParameterGroupLoading } = useProductGroupOptions()

  const handleSelectedListItemDrawerClose = () => {
    setSelectedProductId('')
    setIsSelectedListItemDrawerOpen(false)
  }

  const handleProductsEditDrawerClose = () => {
    searchParams.delete(PRODUCTS_EDIT_DRAWER_TAB_PARAM_KEY)
    setSearchParams(searchParams)
    setIsProductsEditDrawerOpen(false)
  }

  const handleRowClick = (id: string) => {
    setSelectedProductId(id)
    setIsSelectedListItemDrawerOpen(true)
  }

  const handleProductsEditSaveSuccess = () => {
    // Update the product list after successful save
    fetchTariffProductsWithParams()
  }

  const handleTariffProductDelete = (productId: string) => {
    deleteProduct(productId, { mode: ProductDeleteModes.CASCADE })
      .then(unwrapResult)
      .then(() => {
        setSelectedProductId('')
        setIsSelectedListItemDrawerOpen(false)
        fetchTariffProductsWithParams()
        SnackbarUtils.success(t`Product successfully deleted.`)
      })
      .catch(({ message }: ProductTariffError) => {
        if (message) {
          SnackbarUtils.error(message)
        } else {
          SnackbarUtils.error(t`There was an error deleting the product.`)
        }
      })
  }

  const handleWalttiTariffProductDelete = (productId: string) => {
    if (!tariffId) return

    deleteTariffProduct({ tariffId, productId })
      .unwrap()
      .then(() => {
        setSelectedProductId('')
        setIsSelectedListItemDrawerOpen(false)
        fetchTariffProductsWithParams()
        SnackbarUtils.success(t`Product successfully deleted.`)
      })
      .catch(({ data: errorResponse }: { data: ProductTariffError }) => {
        const { message } = errorResponse
        if (message) {
          SnackbarUtils.error(message)
        } else {
          SnackbarUtils.error(t`There was an error deleting the product.`)
        }
      })
  }

  const handleProductDelete = IS_WALTTI_SERVICE_ENVIRONMENT
    ? handleWalttiTariffProductDelete
    : handleTariffProductDelete

  const breadcrumbs = [
    {
      title: t`Home`,
      link: PRODUCT_MANAGEMENT_ROOT_URL
    },
    {
      title: t`Tariffs`,
      link: baseUrl
    },
    {
      title: singleTariff ? singleTariff.description : ''
    },
    {
      title: t`Tariff products`
    }
  ]

  const isTariffProductsLoading = tariffProductsLoading === 'pending'
  const isDeleteProductLoading = deleteProductLoading === 'pending' || isDeleteTariffProductLoading
  const isLoading =
    isTariffProductsLoading ||
    isDeleteProductLoading ||
    isSingleTariffLoading ||
    isCustomerGroupParameterGroupLoading

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ mb: 2 }}>
        <Breadcrumbs items={breadcrumbs} />
        <PageTitle>{t`Tariff products`}</PageTitle>
      </Box>
      <ListActions>
        <Box sx={{ display: 'flex', gap: 2 }}>
          {IS_TARIFF_EDIT_ALLOWED && (
            <CustomButton
              onClick={() => setIsProductsEditDrawerOpen(true)}
              size="small"
              startIcon={<AddIcon />}
            >
              {t`Add products`}
            </CustomButton>
          )}
          {IS_WALTTI_SERVICE_ENVIRONMENT && (
            <CustomButton
              onClick={() => navigate(`${baseUrl}/${tariffId}/sales-channels/create/`)}
              size="small"
              startIcon={<AddIcon />}
              variant="outlined"
            >
              {t`Create new sales channel`}
            </CustomButton>
          )}
        </Box>
        <ListSearch
          searchText={searchText}
          handleSearchTextChange={handleSearchTextChange}
          handleSearch={handleSearch}
          placeholder={t`Search by name or code`}
        />
      </ListActions>
      <TabContext value={tabFromParams}>
        <WithLoader loading={isProductGroupParameterGroupLoading} rows={1} height={33}>
          <TabList onChange={handleTabChange} aria-label={t`Product group tab list`}>
            {productGroupOptions?.map(({ label, value }) => (
              <Tab key={value} label={label} value={value} />
            ))}
          </TabList>
        </WithLoader>
        <Box pt={1}>
          {productGroupOptions?.map(({ value }) => (
            <TabPanel sx={{ p: 0 }} key={value} value={value}>
              <ProductList
                rows={tariffProducts}
                isLoading={isLoading}
                params={searchQueryParams}
                handleRequestSort={handleRequestSort}
                handleChangePage={handleChangePage}
                count={total_results}
                page={
                  searchQueryParams.start_index
                    ? Number.parseInt(searchQueryParams.start_index, 10) / ROWS_PER_PAGE_DEFAULT
                    : 0
                }
                customerGroupsParameter={customerGroupParameterGroup}
                allowSelect={false}
                selectedProductId={selectedProductId}
                onRowClick={handleRowClick}
              />
            </TabPanel>
          ))}
        </Box>
      </TabContext>
      {/* Product details drawer */}
      <UnsavedChangesProvider>
        <TariffProductDrawer
          open={isSelectedListItemDrawerOpen}
          handleClose={handleSelectedListItemDrawerClose}
          handleProductDelete={handleProductDelete}
        />
      </UnsavedChangesProvider>
      {/* Products edit drawer */}
      <TariffProductsEditDrawer
        open={isProductsEditDrawerOpen}
        onClose={handleProductsEditDrawerClose}
        selectedTariff={singleTariff}
        isSingleTariffLoading={isSingleTariffLoading}
        isDeleteProductLoading={isDeleteProductLoading}
        handleSaveSuccess={handleProductsEditSaveSuccess}
      />
    </Box>
  )
}

export default TariffProductListPage
